import { DestroyRef, inject, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { BreakpointObserver } from '@angular/cdk/layout';
import { NavigationEnd, Router } from '@angular/router';
import each from 'lodash-es/each';
import isArray from 'lodash-es/isArray';
import isEqual from 'lodash-es/isEqual';
import keys from 'lodash-es/keys';
import sortBy from 'lodash-es/sortBy';
import { BehaviorSubject } from 'rxjs';
import { SidenavItem } from '@layout/sidenav/sidenav-item/sidenav-item.interface';
import { filter, map, shareReplay } from 'rxjs/operators';

@Injectable({
  providedIn: 'root'
})
export class SidenavService {

  private readonly router = inject(Router);
  private readonly breakpointObserver = inject(BreakpointObserver);
  private readonly destroyRef = inject(DestroyRef);

  private readonly mobileQuery = '(max-width: 959.98px)';

  /**
   * Se a tela é de celular, pela mesma medida que decide o modo "over" da sidenav.
   *
   * <p>Público porque a regra não é só da sidenav: a posição do menu também depende dela, e
   * repetir a media query noutro serviço faria as duas envelhecerem separadas — bastaria alguém
   * ajustar uma para o menu superior voltar a aparecer numa largura em que ele não cabe.</p>
   */
  readonly mobile$ = this.breakpointObserver.observe(this.mobileQuery).pipe(
    map(resultado => resultado.matches),
    shareReplay({ bufferSize: 1, refCount: false }),
  );

  /**
   * Sidenav Items
   */
  private _items = new BehaviorSubject<SidenavItem[]>([]);

  items$ = this._items.asObservable();

  get items(): SidenavItem[] {
    return this._items.getValue();
  }

  set items(items: SidenavItem[]) {
    this._items.next(items);
  }

  /**
   * Currently Open
   */
  private _currentlyOpen = new BehaviorSubject<SidenavItem[]>([]);

  currentlyOpen$ = this._currentlyOpen.asObservable();

  get currentlyOpen(): SidenavItem[] {
    return this._currentlyOpen.getValue();
  }

  set currentlyOpen(currentlyOpen: SidenavItem[]) {
    this._currentlyOpen.next(currentlyOpen);
  }

  /**
   * Aberta por padrão; fechada só no celular, onde ela é "over" e cobriria a tela.
   *
   * <p>O valor inicial estava invertido — abria no celular e fechava no desktop — e só não
   * aparecia porque o `observe` do construtor emite na assinatura e corrigia em seguida.</p>
   */
  private _openSubject = new BehaviorSubject<boolean>(!this.breakpointObserver.isMatched(this.mobileQuery));
  open$ = this._openSubject.asObservable();

  private _modeSubject = new BehaviorSubject<'side' | 'over'>(this.breakpointObserver.isMatched(this.mobileQuery) ? 'over' : 'side');
  mode$ = this._modeSubject.asObservable();

  private _collapsedSubject = new BehaviorSubject<boolean>(false);
  collapsed$ = this._collapsedSubject.asObservable();

  /**
   * Estado atual, para quem precisa dele de uma vez — ver `ThemeService.config`.
   *
   * Em português para não colidir com o `isOpen(item)` privado logo abaixo, que responde outra
   * pergunta: se um item de menu está expandido.
   */
  get estaAberta(): boolean {
    return this._openSubject.getValue();
  }

  get estaRecolhida(): boolean {
    return this._collapsedSubject.getValue();
  }

  private _expandedSubject = new BehaviorSubject<boolean>(false);
  expanded$ = this._expandedSubject.asObservable();

  constructor() {
    this.router.events.pipe(
      filter((event): event is NavigationEnd => event instanceof NavigationEnd),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(event => {
      this.setCurrentlyOpenByRoute(event.url);

      if (this.breakpointObserver.isMatched(this.mobileQuery)) {
        // Close Sidenav on Mobile after Route Change
        this._openSubject.next(false);
      }
    });

    this.breakpointObserver.observe(this.mobileQuery).pipe(
      map(result => result.matches),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(isMobile => {
      if (isMobile) {
        this._openSubject.next(false);
        this._modeSubject.next('over');
        this._collapsedSubject.next(false);
      } else {
        this._openSubject.next(true);
        this._modeSubject.next('side');
      }
    });
  }

  open() {
    this._openSubject.next(true);
  }

  close() {
    this._openSubject.next(false);
  }

  setCollapsed(collapsed: boolean) {
    this._collapsedSubject.next(collapsed);
  }

  toggleCollapsed() {
    this._collapsedSubject.next(!this._collapsedSubject.getValue());
  }

  setExpanded(expanded: boolean) {
    this._expandedSubject.next(expanded);
  }

  toggleExpanded() {
    this._expandedSubject.next(!this._expandedSubject.getValue());
  }

  addItems(items: SidenavItem[]) {
    items.forEach(item => this.addItem(item));
  }

  addItem(item: SidenavItem) {
    const foundIndex = this.items.findIndex((existingItem) => isEqual(existingItem, item));
    if (foundIndex === -1) {
      this.setParentRecursive(item);
      this.items = [...this.items, item];
    }
  }

  toggleItemOpen(item: SidenavItem) {
    let currentlyOpen = this.currentlyOpen;

    if (this.isOpen(item)) {
      if (currentlyOpen.length > 1) {
        currentlyOpen.length = currentlyOpen.indexOf(item);
      } else {
        currentlyOpen = [];
      }
    } else {
      currentlyOpen = this.getParents(item);
    }

    this.currentlyOpen = currentlyOpen;
  }

  sortRecursive(array: SidenavItem[], propertyName: string): SidenavItem[] {
    array.forEach(item => {
      const keyArray = keys(item);
      keyArray.forEach(key => {
        const valor = item[key];
        // A assinatura de índice é unknown: estreita antes de recorrer nas sub-listas.
        if (isArray(valor)) {
          item[key] = this.sortRecursive(valor as SidenavItem[], propertyName);
        }
      });
    });

    return sortBy(array, propertyName);
  }

  getItemByRoute(route: string) {
    return this.getItemByRouteRecursive(route, this.items);
  }

  private getParents(item: SidenavItem, items: SidenavItem[] = []): SidenavItem[] {
    items.unshift(item);

    if (item.parent) {
      return this.getParents(item.parent, items);
    } else {
      return items;
    }
  }

  private isOpen(item: SidenavItem) {
    return (this.currentlyOpen.indexOf(item) > -1);
  }

  private setCurrentlyOpenByRoute(route: string) {
    const item = this.getItemByRouteRecursive(route, this.items);
    let currentlyOpen: SidenavItem[] = [];

    if (item && item.parent) {
      currentlyOpen = this.getParents(item);
    } else if (item) {
      currentlyOpen = [item];
    }

    this.currentlyOpen = currentlyOpen;
  }

  private getItemByRouteRecursive(route: string, collection: SidenavItem[]) {
    let result = collection.find(i => i.routeOrFunction === route);

    if (!result) {
      each(collection, (item) => {
        if (item && item.subItems && item.subItems.length > 0) {
          const found = this.getItemByRouteRecursive(route, item.subItems);

          if (found) {
            result = found;
            return false;
          }
        }
      });
    }

    return result;
  }

  private setParentRecursive(item: SidenavItem) {
    if (item.subItems && item.subItems.length > 0) {
      item.subItems.forEach(subItem => {
        subItem.parent = item;
        this.setParentRecursive(subItem);
      });
    }
  }
}
